import type { ApolloError } from '@apollo/client';
import { getAccountId } from '@seek/adv-account-selection';
import {
  BusinessAddressForm,
  type BusinessAddressRef,
  type Country,
} from '@seek/hirer-business-address';
import {
  type FocusEvent,
  type FormEvent,
  type RefObject,
  useState,
} from 'react';
import { Element } from 'react-scroll';

import { SECTION_BUSINESS_ADDRESS } from 'src/app/shared/constants/pageSectionName';
import { BUSINESS_ADDRESS_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import {
  GetHirerAccountDocument,
  useUpdateBusinessAddressMutation,
} from 'src/graphql/generated';

interface BusinessAddressSectionProps {
  businessAddressRef?: RefObject<BusinessAddressRef>;
  onUpdateFailed: (error: Error) => void;
}

export const BusinessAddressSection: React.FC<BusinessAddressSectionProps> = ({
  businessAddressRef,
  onUpdateFailed,
}) => {
  const { getLanguage } = useConfig();
  const { trackingAdapter } = useAnalyticsTracking();
  const { postalAddress } = useHirerAccount() || {};
  const [updateBusinessAddress] = useUpdateBusinessAddressMutation({
    refetchQueries: [
      {
        query: GetHirerAccountDocument,
        variables: {
          // TO-DO: Remove this when we move to the new hirer account query
          adCentreAdvertiserId: getAccountId(),
        },
      },
    ],
    onError: (error: ApolloError) => {
      onUpdateFailed(error);
    },
  });

  const [addressLines, setAddressLines] = useState('');
  const [state, setState] = useState('');
  const [suburb, setSuburb] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState(postalAddress?.country);

  const onBlur = (
    event: FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { id } = event.target;

    const eventData = {
      eventName: BUSINESS_ADDRESS_PRESSED,
      objectInteraction: id.toLowerCase(),
    };

    trackingAdapter.sendLinkEvent(eventData);
  };

  const onChange = (
    event: FormEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { id, value } = event.target as HTMLInputElement;
    switch (id) {
      case 'addressLines': {
        setAddressLines(value);
        return;
      }
      case 'state': {
        setState(value);
        return;
      }
      case 'suburb': {
        setSuburb(value);
        return;
      }
      case 'postCode': {
        setPostCode(value);
        return;
      }
      case 'country': {
        setCountry(value);
        return;
      }
      default: {
        return;
      }
    }
  };

  const onSubmit = () => {
    updateBusinessAddress({
      variables: {
        input: {
          useForBillingAddress: true,
          addressLines: [addressLines],
          state,
          suburb,
          postCode,
          countryCodeAlpha2: country as Country,
        },
      },
    });
  };

  return (
    <Element name={SECTION_BUSINESS_ADDRESS}>
      <BusinessAddressForm
        businessAddressRef={businessAddressRef}
        language={getLanguage()}
        onSubmit={onSubmit}
        onBlur={onBlur}
        onChange={onChange}
        compactHeading={true}
        address={{
          countryCode: country as Country,
          addressLines: [addressLines],
          suburb,
          state,
          postCode,
        }}
      />
    </Element>
  );
};
